import React from 'react';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';

import {type ISnippet, useLoggedInUser} from 'src/services/api';
import { Flex, FlexColumn } from 'src/components/flex';
import { SnippetsIcon } from 'src/icons';
import { formatPlaybookMessageForFrontend } from 'src/common/playbook-variables/playbook-variable-utils';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/hooks';
import { isSupportedSignatureChannel } from 'src/services/api';
import { getMissingVariable, snippetContentsWithVariablesFilled } from 'src/pages/landlord/leads/messages/components/snippets-modal/utils';
import { PlaybookVariable } from 'src/services/api';

interface Props {
    snippets: ISnippet[];
    setSnippetsModalOpen: (snippetsModalOpen: boolean) => void;
}

export default function SnippetsModalList ({
    snippets,
    setSnippetsModalOpen,
}: Props) {
    const {
        lead,
        property,
        userSettings,
        channel,
        setReplyInput,
        search,
    } = useLeadMessagesPageContext();

    const { data: loggedInUser } = useLoggedInUser();

    const signature = isSupportedSignatureChannel(channel) ? userSettings.signatures[channel] : undefined;
    const isSignatureEnabled = signature?.is_enabled;
    const formattedSignature = isSignatureEnabled ? `\n---\n${signature.text}` : '';
    const leadFirstName = lead.name?.split(' ')[0];
    const propertyAddress = property?.street_address;
    const propertyTourLink = property?.showing_scheduling_link?.link;
    const propertyApplicationLink = property?.application_link?.link;
    const rent = property?.rent ? property.rent.toString() : undefined;
    const numberOfBedrooms = property?.num_bedrooms ? property.num_bedrooms.toString() : undefined;
    const numberOfBathrooms = property?.num_bathrooms ? property.num_bathrooms.toString() : undefined;
    const emailSignature = userSettings?.signatures?.email?.text;
    const smsSignature = userSettings?.signatures?.sms?.text;

    const variableMap: Record<PlaybookVariable, string> = {
        [PlaybookVariable.LeadFirstName]: leadFirstName ?? '',
        [PlaybookVariable.PropertyAddress]: propertyAddress ?? '',
        [PlaybookVariable.PropertyTourLink]: propertyTourLink ?? '',
        [PlaybookVariable.PropertyApplicationLink]: propertyApplicationLink ?? '',
        [PlaybookVariable.Rent]: rent ?? '',
        [PlaybookVariable.NumberOfBedrooms]: numberOfBedrooms ?? '',
        [PlaybookVariable.NumberOfBathrooms]: numberOfBathrooms ?? '',
        [PlaybookVariable.SMSSignature]: smsSignature ?? '',
        [PlaybookVariable.EmailSignature]: emailSignature ?? '',
        [PlaybookVariable.YourName]: loggedInUser?.name ?? '',
        [PlaybookVariable.YourFirstName]: loggedInUser?.name?.split(' ')[0] ?? '',
    };

    const hasRequiredVariables = (snippetContents: string) => {
        return !getMissingVariable(
            snippetContents,
            variableMap,
        );
    };

    const handleClickSnippet = (snippet: ISnippet) => {
        setReplyInput(snippetContentsWithVariablesFilled(
            snippet.contents,
            variableMap,
        ) + '\n' + formattedSignature);
        setSnippetsModalOpen(false);
    };

    return (
        <FlexColumn>
            {snippets.length === 0 &&
                <Flex justifyContent="center">
                    <Typography variant="subtitle2">
                        {search ? 'No snippets match your search.' :'You do not have any snippets at the moment.'}
                    </Typography>
                </Flex>
            }

            {snippets.map((snippet) => {
                const snippetContents = snippet.contents;
                const isDisabled = !hasRequiredVariables(snippetContents);
                const missingVariable = getMissingVariable(
                    snippetContents,
                    variableMap,
                );

                return (
                    <>
                        <Tooltip
                            title={isDisabled ? `Missing info to use this snippet: ${missingVariable}` : ''}
                        >
                            <Flex
                                key={snippet.id}
                                alignItems="flex-start"
                                onClick={() => !isDisabled && handleClickSnippet(snippet)}
                                sx={{
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    padding: '10px 0',
                                    opacity: isDisabled ? 0.5 : 1,
                                    '&:hover': {
                                        backgroundColor: isDisabled ? 'transparent' : '#f5f5f5',
                                    },
                                }}
                            >
                                <SnippetsIcon size="small" />
                                <FlexColumn gap={0}>
                                    <Typography
                                        width={
                                            {
                                                xs: 250,
                                                sm: 500,
                                                md: 500,
                                                lg: 500,
                                                xl: 500,
                                            }
                                        }
                                        className="ellipsisText"
                                        noWrap
                                        sx={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {snippet.name}
                                    </Typography>
                                    <Typography
                                        width={
                                            {
                                                xs: 250,
                                                sm: 450,
                                                md: 500,
                                                lg: 500,
                                                xl: 500,
                                            }
                                        }
                                        className="ellipsisText"
                                        noWrap
                                        sx={{
                                            fontSize: '12px',
                                            fontWeight: 400,
                                        }}
                                    >
                                        {formatPlaybookMessageForFrontend(snippet.contents)}
                                    </Typography>
                                </FlexColumn>

                            </Flex>
                        </Tooltip>
                        <Divider />
                    </>
                );
            })}
        </FlexColumn>
    );
}
